import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Package, 
  ShoppingCart, 
  X,
  Plus,
  Settings,
  ExternalLink,
  Tag,
  Truck,
  FileText,
  Globe,
  Mail,
  Plane
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import VariantManager from '../components/admin/VariantManager';
import PromoCodeManager from '../components/admin/PromoCodeManager';
import CategoryManager from '../components/admin/CategoryManager';
import ImageUploader from '../components/admin/ImageUploader';
import { OverviewTab, InventoryTab, OrdersTab, ContentTab, SubscribersTab } from '../components/admin/tabs';
import { TravelInquiriesTab } from '../components/admin/TravelInquiriesTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { isAdmin, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [inventory, setInventory] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editProduct, setEditProduct] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [categories, setCategories] = useState([]);
  const [newProductData, setNewProductData] = useState({
    sku: '',
    name: '',
    category: '',
    price: '',
    quantity_in_stock: 50,
    reorder_threshold: 10,
    short_description: '',
    description: '',
    ingredients: '',
    how_to_use: '',
    image_url: ''
  });
  const [siteContent, setSiteContent] = useState(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [savingContent, setSavingContent] = useState(false);
  const [contentTab, setContentTab] = useState('site');
  
  // Site Status (construction mode)
  const [siteStatus, setSiteStatus] = useState({
    under_construction: false,
    construction_message: 'This website is under construction.',
    redirect_url: 'https://livelifecompounds.com'
  });
  const [savingSiteStatus, setSavingSiteStatus] = useState(false);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories?active_only=false`);
        setCategories(response.data);
      } catch (error) {
        // Silent fail - categories won't populate dropdown
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    if (authLoading) return;
    
    if (!isAdmin) {
      navigate('/');
      return;
    }
    fetchData();
  }, [isAdmin, authLoading, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [inventoryRes, ordersRes] = await Promise.all([
        axios.get(`${API}/admin/inventory`),
        axios.get(`${API}/admin/orders`)
      ]);
      setInventory(inventoryRes.data);
      setOrders(ordersRes.data);
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const fetchContent = async () => {
    setContentLoading(true);
    try {
      const response = await axios.get(`${API}/admin/content`);
      setSiteContent(response.data.content);
    } catch (error) {
      toast.error('Failed to load site content');
    } finally {
      setContentLoading(false);
    }
  };

  // Fetch site status
  const fetchSiteStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/site-status`);
      setSiteStatus(response.data);
    } catch (error) {
      // Silent fail - site status won't update
    }
  };

  // Save site status
  const saveSiteStatus = async () => {
    setSavingSiteStatus(true);
    try {
      await axios.put(`${API}/admin/site-status`, siteStatus);
      toast.success(siteStatus.under_construction ? 'Site set to Under Construction' : 'Site is now LIVE!');
    } catch (error) {
      toast.error('Failed to update site status');
    } finally {
      setSavingSiteStatus(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'content' && !siteContent) {
      fetchContent();
      fetchSiteStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleUpdateProduct = async () => {
    try {
      await axios.put(`${API}/admin/products/${editProduct.id}`, editFormData);
      toast.success('Product updated successfully');
      setEditProduct(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update product');
    }
  };

  const handleAddProduct = async () => {
    if (!newProductData.sku || !newProductData.name || !newProductData.price) {
      toast.error('Please fill in SKU, Name, and Price');
      return;
    }

    try {
      await axios.post(`${API}/admin/products`, {
        ...newProductData,
        price: parseFloat(newProductData.price),
        quantity_in_stock: parseInt(newProductData.quantity_in_stock) || 50,
        reorder_threshold: parseInt(newProductData.reorder_threshold) || 10
      });
      toast.success('Product added successfully');
      setShowAddProduct(false);
      setNewProductData({
        sku: '',
        name: '',
        category: 'teas',
        price: '',
        quantity_in_stock: 50,
        reorder_threshold: 10,
        short_description: '',
        description: '',
        ingredients: '',
        how_to_use: '',
        image_url: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add product');
    }
  };

  const handleDeleteProduct = async (productId, productName) => {
    if (!window.confirm(`Are you sure you want to delete "${productName}"?`)) return;

    try {
      await axios.delete(`${API}/admin/products/${productId}`);
      toast.success('Product deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete product');
    }
  };

  const handleUpdateStock = async (productId, newStock) => {
    try {
      await axios.put(`${API}/admin/products/${productId}`, { quantity_in_stock: parseInt(newStock) });
      toast.success('Stock updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update stock');
    }
  };

  const handleUpdateOrderStatus = async (orderId, status, trackingNumber = null) => {
    try {
      let url = `${API}/admin/orders/${orderId}/status?status=${status}`;
      if (trackingNumber) {
        url += `&tracking_number=${encodeURIComponent(trackingNumber)}`;
      }
      await axios.put(url);
      toast.success('Order status updated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update order');
      throw error;
    }
  };

  const handleDeleteOrder = async (orderId) => {
    try {
      await axios.delete(`${API}/admin/orders/${orderId}`);
      toast.success('Order deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete order');
    }
  };

  const openEditModal = (product) => {
    setEditProduct(product);
    setEditFormData({
      name: product.name,
      price: product.price,
      quantity_in_stock: product.quantity_in_stock,
      reorder_threshold: product.reorder_threshold,
      short_description: product.short_description || '',
      description: product.description || '',
      ingredients: product.ingredients || '',
      how_to_use: product.how_to_use || '',
      image_url: product.image_url || ''
    });
  };

  // Content update helpers
  const updateContent = (path, value) => {
    const keys = path.split('.');
    setSiteContent(prev => {
      const newContent = JSON.parse(JSON.stringify(prev));
      let current = newContent;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newContent;
    });
  };

  const getContent = (path, defaultValue = '') => {
    if (!siteContent) return defaultValue;
    const keys = path.split('.');
    let current = siteContent;
    for (const key of keys) {
      if (current === undefined || current === null) return defaultValue;
      current = current[key];
    }
    return current ?? defaultValue;
  };

  const handleSaveContent = async () => {
    setSavingContent(true);
    try {
      await axios.put(`${API}/admin/content`, { content: siteContent });
      toast.success('All changes saved successfully!');
    } catch (error) {
      toast.error('Failed to save content');
    } finally {
      setSavingContent(false);
    }
  };

  const filteredProducts = inventory?.all_products?.filter(product =>
    product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    product.sku.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered': return 'bg-green-100 text-green-700';
      case 'shipped': return 'bg-blue-100 text-blue-700';
      case 'processing': return 'bg-terracotta/20 text-terracotta';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-stone-100 text-stone-700';
    }
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-terracotta border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAdmin) return null;

  return (
    <div className="animate-fadeIn" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <h1 className="font-heading text-3xl md:text-4xl text-text-primary">
            Admin Dashboard
          </h1>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {[
            { id: 'overview', label: 'Overview', icon: null },
            { id: 'travel', label: 'Travel Inquiries', icon: Plane },
            { id: 'inventory', label: 'Products', icon: Package },
            { id: 'categories', label: 'Categories', icon: Settings },
            { id: 'orders', label: 'Orders', icon: ShoppingCart },
            { id: 'promos', label: 'Promo Codes', icon: Tag },
            { id: 'subscribers', label: 'Email List', icon: Mail },
            { id: 'content', label: 'Site Content', icon: Globe }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-2 rounded-full font-medium transition-all whitespace-nowrap flex items-center gap-2 ${
                activeTab === tab.id
                  ? 'bg-terracotta text-white'
                  : 'bg-white text-text-secondary hover:bg-sand'
              }`}
              data-testid={`admin-tab-${tab.id}`}
            >
              {tab.icon && <tab.icon className="w-4 h-4" />}
              {tab.label}
            </button>
          ))}
        </div>

        {loading && activeTab !== 'content' ? (
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-sand rounded-xl animate-pulse" />
            ))}
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <OverviewTab inventory={inventory} orders={orders} />
            )}

            {/* Travel Inquiries Tab */}
            {activeTab === 'travel' && (
              <TravelInquiriesTab />
            )}

            {/* Inventory Tab */}
            {activeTab === 'inventory' && (
              <InventoryTab 
                inventory={inventory}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                onOpenAddProduct={() => setShowAddProduct(true)}
                onOpenEditModal={openEditModal}
                onUpdateStock={handleUpdateStock}
                onDeleteProduct={handleDeleteProduct}
              />
            )}

            {/* Orders Tab */}
            {activeTab === 'orders' && (
              <OrdersTab 
                orders={orders} 
                onUpdateStatus={handleUpdateOrderStatus}
                onDeleteOrder={handleDeleteOrder}
                onRefresh={fetchData}
              />
            )}

            {/* Promo Codes Tab */}
            {activeTab === 'promos' && (
              <PromoCodeManager />
            )}

            {/* Categories Tab */}
            {activeTab === 'categories' && (
              <CategoryManager />
            )}

            {/* Subscribers/Email List Tab */}
            {activeTab === 'subscribers' && (
              <SubscribersTab />
            )}

            {/* Content Management Tab */}
            {activeTab === 'content' && (
              <ContentTab />
            )}
          </>
        )}

        {/* Edit Product Modal */}
        <Dialog open={!!editProduct} onOpenChange={() => setEditProduct(null)}>
          <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Edit Product</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <ImageUploader label="Product Image" currentImage={editFormData.image_url} onImageChange={(url) => setEditFormData({ ...editFormData, image_url: url })} />
              <div>
                <Label>Product Name</Label>
                <Input value={editFormData.name || ''} onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })} className="rounded-xl mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Price ($)</Label>
                  <Input type="number" step="0.01" value={editFormData.price || ''} onChange={(e) => setEditFormData({ ...editFormData, price: parseFloat(e.target.value) })} className="rounded-xl mt-1" />
                </div>
                <div>
                  <Label>Stock</Label>
                  <Input type="number" value={editFormData.quantity_in_stock || ''} onChange={(e) => setEditFormData({ ...editFormData, quantity_in_stock: parseInt(e.target.value) })} className="rounded-xl mt-1" />
                </div>
              </div>
              <div>
                <Label>Short Description</Label>
                <Input value={editFormData.short_description || ''} onChange={(e) => setEditFormData({ ...editFormData, short_description: e.target.value })} className="rounded-xl mt-1" />
              </div>
              <div>
                <Label>Full Description</Label>
                <Textarea value={editFormData.description || ''} onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })} className="rounded-xl mt-1" rows={3} />
              </div>
              <div>
                <Label>Ingredients</Label>
                <Textarea value={editFormData.ingredients || ''} onChange={(e) => setEditFormData({ ...editFormData, ingredients: e.target.value })} className="rounded-xl mt-1" rows={2} />
              </div>
              
              {/* Product Variants */}
              <VariantManager
                product={editProduct}
                onUpdate={(variantData) => setEditFormData({ ...editFormData, ...variantData })}
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setEditProduct(null)} className="rounded-full">Cancel</Button>
              <Button onClick={handleUpdateProduct} className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white">Save</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Product Modal */}
        <Dialog open={showAddProduct} onOpenChange={setShowAddProduct}>
          <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Add New Product</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <ImageUploader label="Product Image" currentImage={newProductData.image_url} onImageChange={(url) => setNewProductData({ ...newProductData, image_url: url })} />
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>SKU *</Label>
                  <Input value={newProductData.sku} onChange={(e) => setNewProductData({ ...newProductData, sku: e.target.value.toUpperCase() })} className="rounded-xl mt-1" placeholder="e.g., TEA-005" />
                </div>
                <div>
                  <Label>Category *</Label>
                  <Select value={newProductData.category} onValueChange={(value) => setNewProductData({ ...newProductData, category: value })}>
                    <SelectTrigger className="rounded-xl mt-1"><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.slug}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Product Name *</Label>
                <Input value={newProductData.name} onChange={(e) => setNewProductData({ ...newProductData, name: e.target.value })} className="rounded-xl mt-1" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Price ($) *</Label>
                  <Input type="number" step="0.01" value={newProductData.price} onChange={(e) => setNewProductData({ ...newProductData, price: e.target.value })} className="rounded-xl mt-1" />
                </div>
                <div>
                  <Label>Initial Stock</Label>
                  <Input type="number" value={newProductData.quantity_in_stock} onChange={(e) => setNewProductData({ ...newProductData, quantity_in_stock: e.target.value })} className="rounded-xl mt-1" />
                </div>
                <div>
                  <Label>Reorder At</Label>
                  <Input type="number" value={newProductData.reorder_threshold} onChange={(e) => setNewProductData({ ...newProductData, reorder_threshold: e.target.value })} className="rounded-xl mt-1" />
                </div>
              </div>
              <div>
                <Label>Short Description</Label>
                <Input value={newProductData.short_description} onChange={(e) => setNewProductData({ ...newProductData, short_description: e.target.value })} className="rounded-xl mt-1" />
              </div>
              <div>
                <Label>Full Description</Label>
                <Textarea value={newProductData.description} onChange={(e) => setNewProductData({ ...newProductData, description: e.target.value })} className="rounded-xl mt-1" rows={3} />
              </div>
              
              {/* Product Variants */}
              <VariantManager
                product={newProductData}
                onUpdate={(variantData) => setNewProductData({ ...newProductData, ...variantData })}
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowAddProduct(false)} className="rounded-full">Cancel</Button>
              <Button onClick={handleAddProduct} className="rounded-full bg-forest hover:bg-forest-dark text-white"><Plus className="w-4 h-4 mr-2" />Add Product</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminDashboard;
